import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';

import AppState from '@webapp/store/state';

import Typography from '@components/basics/typography';
import Invite from '@components/basics/invite';
import { TracInviteTypes } from '@services/invites/types/invite-types';
import { TracInvite } from '@services/invites/types/trac-invite';
import {
  GetInvites,
  CancelInvite,
  ResendInvite,
  AcceptInvite,
  RejectInvite,
  GetRecordLabelInvites,
} from '../../../store/invites/action-creators';
import { currentInviteList } from '../../../store/invites/selectors';
import LoadingCtrl from '@webapp/controllers/loading';
import { GET_INVITES } from '@webapp/store/invites/actions';
import { AnimatePresence } from 'framer-motion';
import MotionOpacity from '@components/motion/motion-opacity';

interface InvitesListCtrlProps {
  className?: string;
  title?: string;
  type?: TracInviteTypes;
  recordLabelId?: string;
}

interface StateProps {
  invites: TracInvite[];
  isLoading: boolean;
  artistId?: string;
  listInvites: (inviterId: string, type: TracInviteTypes) => GetInvites;
  listRecordLabelInvites: (
    type: TracInviteTypes
  ) => GetRecordLabelInvites | undefined;
  cancel: (invite: TracInvite) => CancelInvite;
  resend: (invite: TracInvite) => ResendInvite;
  accept: (invite: TracInvite) => AcceptInvite;
  reject: (invite: TracInvite) => RejectInvite;
}

const mapStateToProps = (
  state: AppState,
  props: InvitesListCtrlProps
): Pick<StateProps, 'invites' | 'isLoading' | 'artistId'> => ({
  invites: props.type
    ? props.recordLabelId
      ? state.invites.recordLabel[props.type].filter(
          (i) => i.status !== 'accepted'
        )
      : currentInviteList(state, props.type).filter(
          (i) => i.status !== 'accepted'
        )
    : state.invites.user,
  isLoading: !!state.ui.loading[GET_INVITES],
  artistId: state.artist.active?.id,
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  props: InvitesListCtrlProps
) => ({
  listInvites: (inviterId: string, type: TracInviteTypes) =>
    dispatch(new GetInvites(inviterId, type)),
  listRecordLabelInvites: (type: TracInviteTypes) => {
    return props.recordLabelId && props.recordLabelId.length > 0
      ? dispatch(new GetRecordLabelInvites(props.recordLabelId, type))
      : undefined;
  },
  cancel: (invite: TracInvite) => dispatch(new CancelInvite(invite)),
  resend: (invite: TracInvite) => dispatch(new ResendInvite(invite)),
  accept: (invite: TracInvite) => dispatch(new AcceptInvite(invite)),
  reject: (invite: TracInvite) => dispatch(new RejectInvite(invite)),
});

const InvitesListCtrl: React.FC<InvitesListCtrlProps & StateProps> = ({
  recordLabelId,
  type,
  listInvites,
  listRecordLabelInvites,
  artistId,
  ...props
}) => {
  React.useEffect(() => {
    if (type && artistId && !recordLabelId) {
      listInvites(artistId, type);
    } else if (type && recordLabelId) {
      listRecordLabelInvites(type);
    }
  }, [listInvites, type, artistId, recordLabelId, listRecordLabelInvites]);

  if (!(props.isLoading || props.invites.length)) {
    return null;
  }

  const actionsAccepted = (invite: TracInvite) => [
    <Typography type="link" weight="700" onClick={() => props.cancel(invite)}>
      Remove Member
    </Typography>,
  ];

  const actionsPending = (invite: TracInvite) => [
    <Typography type="link" weight="700" onClick={() => props.cancel(invite)}>
      Cancel
    </Typography>,
    <Typography type="link" weight="700" onClick={() => props.resend(invite)}>
      Resend
    </Typography>,
  ];

  const actionsUser = (invite: TracInvite) => [
    <Typography type="link" weight="700" onClick={() => props.reject(invite)}>
      Reject
    </Typography>,
    <Typography type="link" weight="700" onClick={() => props.accept(invite)}>
      Accept
    </Typography>,
  ];

  const handleMenu = (action: string, invite: TracInvite) => {
    switch (action) {
      case 'cancel-invite':
        props.cancel(invite);
        return;
      case 'resend-invite':
        props.resend(invite);
        return;
      case 'accept-invite':
        props.accept(invite);
        return;
      case 'reject-invite':
        props.reject(invite);
        return;
      default:
        return;
    }
  };

  const getAction = (invite: TracInvite) => {
    if (!type && invite.status === 'pending') {
      return actionsUser(invite);
    }

    if (type && invite.status === 'accepted') {
      return actionsAccepted(invite);
    }

    if (type && invite.status === 'pending') {
      return actionsPending(invite);
    }

    return undefined;
  };

  const getLabel = (invite: TracInvite): string => {
    if (!type) {
      return invite.metadata?.artist?.name || '';
    }

    return invite.name || invite.email_or_phone;
  };

  return (
    <StyledInvites className={props.className}>
      <AnimatePresence>
        {props.isLoading ? (
          <LoadingCtrl
            className="loading"
            size="80px"
            loading={props.isLoading}
          />
        ) : (
          <MotionOpacity>
            {props.invites.map((invite) => (
              <Invite
                key={invite.id}
                className="invites-item"
                status={invite.status}
                label={getLabel(invite)}
                actions={getAction(invite)}
                handleMenu={(action1, invite1) => handleMenu(action1, invite1)}
                invite={invite}
                type={type}
              />
            ))}
          </MotionOpacity>
        )}
      </AnimatePresence>
    </StyledInvites>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(InvitesListCtrl);

const StyledInvites = styled.div`
  margin-top: 5px;

  .loading > div {
    margin: auto;
  }

  .invites-item {
    margin-bottom: 5px;

    &:last-child {
      margin: 0;
    }
  }
`;
